import React, { ReactNode, useState } from 'react';
import {
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Box,
  Tooltip,
} from '@mui/material';
import { CellProps } from 'react-table';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import { Chip, Text } from '../../../atoms';
import { materialTheme, isObject } from '../../../particles';

export type TableCellProps = CellProps<TableRow, TableCell>;

// Expandable text component for long content
const ExpandableText: React.FC<{ text: string; maxLength?: number }> = ({ text, maxLength = 50 }) => {
  const [expanded, setExpanded] = useState(false);
  const shouldTruncate = text.length > maxLength;
  const displayText = expanded || !shouldTruncate ? text : `${text.substring(0, maxLength)}...`;

  if (!shouldTruncate) {
    return <Text $fontWeight={400} $fontSize={16}>{text}</Text>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
      <Text $fontWeight={400} $fontSize={16} sx={{ flex: 1, minWidth: 0 }}>
        {displayText}
      </Text>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        sx={{ p: 0.5, flexShrink: 0 }}
      >
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </IconButton>
    </Box>
  );
};

/**
 * Renders an action cell as a compact three-dot (MoreVert) icon that
 * opens a dropdown listing the actions. Used when a row has many
 * actions and the wide icon-row layout would crowd the table (Bills).
 * Each menu item shows the action's icon + tooltip text.
 */
const ActionsMenuCell: React.FC<{ items: TableCellAction['data'] }> = ({ items }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);

  return (
    <React.Fragment>
      <IconButton
        aria-label="row actions"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(event) => event.stopPropagation()}
      >
        {items.map((item, index) => {
          const label = item.tooltip ?? '';
          const IconEl =
            item.type === 'delete'
              ? DeleteIcon
              : item.type === 'edit'
              ? EditIcon
              : item.type === 'signout'
              ? LogoutIcon
              : item.icon;
          const iconColor =
            item.disabled
              ? 'disabled'
              : item.type === 'delete'
              ? 'error'
              : item.type === 'signout'
              ? 'warning'
              : 'inherit';
          return (
            <MenuItem
              key={`menu_action_${index}`}
              disabled={item.disabled}
              onClick={(event) => {
                event.stopPropagation();
                if (item.disabled) return;
                close();
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                item.linkData?.onClick?.(event);
              }}
            >
              {IconEl && (
                <ListItemIcon>
                  <IconEl fontSize="small" color={iconColor as 'inherit' | 'disabled' | 'error' | 'warning'} />
                </ListItemIcon>
              )}
              <ListItemText primary={label} />
            </MenuItem>
          );
        })}
      </Menu>
    </React.Fragment>
  );
};

const renderCellType = (columnId: string, cellTypeData: TableCellType) => {
  const { type } = cellTypeData;

  switch (type) {
    case `chip`: {
      const chipData = cellTypeData as TableCellChip;
      const { data } = chipData;

      return (
        <Grid direction={'row'} rowSpacing={1}>
          {data.map((item, index) => {
            const { text, backgroundColor, size } = item;
            const textColor =
              backgroundColor && materialTheme.palette.getContrastText(backgroundColor);
            return (
              <Chip
                key={index}
                label={text}
                size={size || 'medium'}
                sx={{ ml: index > 0 ? 1 : 0 }}
                {...(backgroundColor && { $backgroundColor: backgroundColor })}
                {...(textColor && { $textColor: textColor })}
              />
            );
          })}
        </Grid>
      );
    }

    case `text`: {
      const textData = cellTypeData as TableCellText;
      const { data } = textData;

      if (!data) return <React.Fragment />;

      const textNode = (
        <Text $fontWeight={400} $fontSize={16} $fontColor={data.fontColor}>
          <ExpandableText text={data.text} maxLength={50} />
        </Text>
      );

      if (data.copyable && data.text) {
        const handleCopy = (event: React.MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation();
          navigator.clipboard.writeText(data.text);
          toast.success('Copied to clipboard');
        };
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {textNode}
            <Tooltip title="Copy">
              <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }

      return textNode;
    }
    case `action`: {
      const textData = cellTypeData as TableCellAction;
      const { data } = textData;

      if (textData.renderAs === 'menu') {
        return <ActionsMenuCell items={data} />;
      }

      return (
        <Stack direction={'row'}>
          {data.map((item, index) => {
            const button = (
              <span>
                <IconButton
                  key={`action_button_${index}`}
                  aria-label="delete"
                  sx={{ mr: 2, width: 40 }}
                  disabled={item.disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (item.disabled) return;
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    //@ts-ignore
                    item.linkData?.onClick?.(event);
                  }}
                >
                  {item.type == 'delete' && <DeleteIcon color={item.disabled ? 'disabled' : 'error'} />}
                  {item.type == 'edit' && <EditIcon />}
                  {item.type == 'signout' && <LogoutIcon color={item.disabled ? 'disabled' : 'warning'} />}
                  {item.type == 'other' && item.icon && <item.icon />}
                </IconButton>
              </span>
            );
            return item.tooltip ? (
              <Tooltip key={`action_button_wrap_${index}`} title={item.tooltip}>{button}</Tooltip>
            ) : (
              <React.Fragment key={`action_button_wrap_${index}`}>{button}</React.Fragment>
            );
          })}
        </Stack>
      );
    }
    case `switch`: {
      const textData = cellTypeData as TableCellSwitch;
      const { data } = textData;

      const [checked, setChecked] = useState<boolean>(data.selected);

      return (
        <React.Fragment>
          <Stack direction={'row'} alignItems={'center'}>
            <Switch
              color="secondary"
              checked={checked}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                event.stopPropagation();
                setChecked(event.target.checked);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                data.linkData?.onClick(event.target.checked);
              }}
            />
            <Text $fontWeight={400} $fontSize={12}>
              {checked ? data.enable : data.disable}
            </Text>
          </Stack>
        </React.Fragment>
      );
    }
    case `selector`: {
      const textData = cellTypeData as TableCellSelector;
      const { data } = textData;
      const [selected, setSelected] = useState<string | null>(data.selected);
      return (
        <React.Fragment>
          <FormControl fullWidth size="small">
            <Select
              value={selected != null ? selected : '0'}
              onChange={(event: SelectChangeEvent) => {
                const value = event.target.value;
                setSelected(value);

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                data.linkData?.onClick(value == 0 ? null : value);
              }}
            >
              <MenuItem value={0}>None</MenuItem>
              <MenuItem value={'applicant'}>Applicant</MenuItem>
              <MenuItem value={'contact'}>Contact</MenuItem>
              <MenuItem value={'landlord'}>Landlord</MenuItem>
              <MenuItem value={'tenancy'}>Tenancy</MenuItem>
              <MenuItem value={'vendor'}>Vendor</MenuItem>
            </Select>
          </FormControl>
        </React.Fragment>
      );
    }
  }
};

export const TableCell: React.FC<TableCellProps> = (props: TableCellProps): React.ReactElement => {
  const {
    value: cellData,
    cell: {
      column: { id }
    }
  } = props;

  if (isObject(cellData)) {
    return <React.Fragment>{renderCellType(id, cellData as TableCellType)}</React.Fragment>;
  } else {
    const textValue = cellData?.toString() || '';
    return (
      <React.Fragment>
        <ExpandableText text={textValue} maxLength={50} />
      </React.Fragment>
    );
  }
};

export default TableCell;
